import {
  format,
  isSameDay,
  differenceInMinutes,
  addDays as dfAddDays,
  startOfISOWeek,
  isWeekend,
} from 'date-fns'

export const HOURS_PER_DAY = 8

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

/**
 * Formats a Date to 'yyyy-MM-dd' string key.
 *
 * @param {Date} date - Date to format
 * @returns {string} Formatted date key
 */
export const dateKey = (date) => format(date, 'yyyy-MM-dd')

/**
 * Combines row ID and date key into a unique cell ID string.
 *
 * @param {string} rowId - Row or staff identifier
 * @param {string} key - Date key string
 * @returns {string} Unique cell identifier
 */
export const cellId = (rowId, key) => `${rowId}|${key}`

/**
 * Returns the Monday-based week start for a given date.
 *
 * @param {Date} date - Input date
 * @returns {Date} Monday of the current ISO week
 */
export function startOfWeek(date) {
  return startOfISOWeek(date)
}

/**
 * Adds or subtracts days from a given date.
 *
 * @param {Date} date - Base date
 * @param {number} n - Number of days to add
 * @returns {Date} Resulting date
 */
export function addDays(date, n) {
  return dfAddDays(date, n)
}

/**
 * Builds an array of sequential days starting from a start date.
 *
 * @param {Date} start - Starting date
 * @param {number} count - Total days to build
 * @returns {Date[]} Array of Date instances
 */
export function buildDays(start, count) {
  const days = []
  for (let i = 0; i < count; i++) days.push(dfAddDays(start, i))
  return days
}

/**
 * Formats header metadata for a calendar day.
 *
 * @param {Date} date - Day date
 * @returns {{ weekday: string, dayMonth: string, isWeekend: boolean, isToday: boolean }} Header display properties
 */
export function formatDayHeader(date) {
  return {
    weekday:   format(date, 'EEE'),
    dayMonth:  format(date, 'd MMM'),
    isWeekend: isWeekend(date),
    isToday:   isSameDay(date, new Date()),
  }
}

/**
 * Formats a date range into a human-readable string.
 *
 * @param {Date} start - Start of range
 * @param {Date} end - End of range
 * @returns {string} Formatted range string
 */
export function formatRange(start, end) {
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
}

/**
 * Formats a 'yyyy-MM-dd' key into a full weekday date string.
 *
 * @param {string} key - Date key in 'yyyy-MM-dd' format
 * @returns {string} Formatted full date string
 */
export function formatFullDate(key) {
  const [y, m, d] = key.split('-').map(Number)
  return format(new Date(y, m - 1, d), 'EEEE, d MMM yyyy')
}

/**
 * Returns existing schedule for a person on a day.
 * scheduleMap: Map<"userId:yyyy-MM-dd", { status, bookedHours, capacityHours, project, projectId, projectIds }>
 * Cells not in the map = free (no allocations for that day).
 */
const freeEntry = (capacityHours) => ({ status: 'free', bookedHours: 0, capacityHours, project: null, projectId: null })
const offEntry  = (capacityHours) => ({ status: 'off',  bookedHours: 0, capacityHours, project: null, projectId: null })

/**
 * Returns account's configured standard day length from letterhead, falling back to HOURS_PER_DAY.
 *
 * @param {Object} [letterhead] - Account letterhead configuration
 * @returns {number} Standard daily capacity hours
 */
export function getStandardHours(letterhead) {
  const parsed = parseFloat(letterhead?.total_hrs)
  if (!Number.isFinite(parsed) || parsed <= 0) return HOURS_PER_DAY
  return Math.round(parsed * 10) / 10
}

/**
 * Extracts set of available weekday names from User.available_days.
 *
 * @param {Object} [available_days] - User availability record
 * @returns {Set<string>|null} Set of lowercase weekday names, or null if unset
 */
export function availableDaysFromUserRecord(available_days) {
  const flags = available_days?.days
  if (!flags) return null
  const days = new Set()
  DAY_NAMES.forEach((day) => {
    if (flags[day]) days.add(day)
  })
  return days
}

/**
 * Checks if a staff member is available to work on a specific date.
 *
 * @param {Map<string, Set<string>>} [availability] - Staff availability map
 * @param {string} staffId - Staff user ID
 * @param {Date} date - Target date instance
 * @returns {boolean} True if available or status unknown
 */
export function isDayAvailable(availability, staffId, date) {
  const days = availability?.get(staffId)
  if (!days) return true
  return days.has(DAY_NAMES[date.getDay()])
}

/**
 * Flattens work-hour setting's date_exceptions into a date-indexed Map.
 *
 * @param {Object} [setting] - Work hour setting record
 * @returns {Map<string, { name: string, type: ?string }>} Map of date exceptions
 */
export function exceptionDatesFromSetting(setting) {
  const map = new Map()
  ;(setting?.date_exceptions || []).forEach((item) => {
    if (item?.date && !map.has(item.date)) {
      map.set(item.date, { name: item.name || 'Exception', type: item.exception_type || null })
    }
  })
  return map
}

/**
 * Retrieves exception info ({ name, type }) for staffId on a given day key.
 *
 * @param {Map<string, Map<string, Object>>} [exceptionDates] - Nested map of exception dates
 * @param {string} staffId - Staff user ID
 * @param {string} key - Day key string ('yyyy-MM-dd')
 * @returns {{ name: string, type: ?string }|null} Exception details or null
 */
export function getException(exceptionDates, staffId, key) {
  return exceptionDates?.get(staffId)?.get(key) || null
}

/**
 * Resolves the schedule state for a staff member on a specific day.
 *
 * @param {Map} scheduleMap - Map of booked schedules keyed by "userId:yyyy-MM-dd"
 * @param {string} staffId - Staff user ID
 * @param {string} key - Day key in 'yyyy-MM-dd' format
 * @param {Map} [availability] - Staff availability map
 * @param {number} [standardHours=HOURS_PER_DAY] - Standard daily capacity hours
 * @returns {{ status: string, bookedHours: number, capacityHours: number, project: ?string, projectId: ?string, projectIds: ?string[] }} Day schedule entry
 */
export function getDaySchedule(scheduleMap, staffId, key, availability, standardHours = HOURS_PER_DAY) {
  // A real booking always wins over "not scheduled to work" (e.g. a picked-up Saturday shift).
  const existing = scheduleMap.get(`${staffId}:${key}`)
  if (existing) return existing

  const [y, m, d] = key.split('-').map(Number)
  if (!isDayAvailable(availability, staffId, new Date(y, m - 1, d))) return offEntry(standardHours)
  return freeEntry(standardHours)
}

/**
 * Checks if a staff member has existing booked hours on a given day.
 *
 * @param {Map} scheduleMap - Map of booked schedules
 * @param {string} staffId - Staff user ID
 * @param {string} key - Day key in 'yyyy-MM-dd' format
 * @param {Map} [availability] - Staff availability map
 * @param {number} [standardHours] - Standard daily capacity hours
 * @returns {boolean} True if the staff member has booked hours
 */
export function hasConflict(scheduleMap, staffId, key, availability, standardHours) {
  return getDaySchedule(scheduleMap, staffId, key, availability, standardHours).bookedHours > 0
}

/**
 * Returns roster members already allocated to a specific project on a given day.
 *
 * @param {Map} scheduleMap - Map of booked schedules
 * @param {string} projectId - Target project UUID
 * @param {string} key - Day key string ('yyyy-MM-dd')
 * @param {Array<Object>} roster - List of staff roster members
 * @returns {Array<Object>} Filtered staff roster members
 */
export function getProjectDayStaff(scheduleMap, projectId, key, roster) {
  return roster.filter((s) => {
    const entry = scheduleMap.get(`${s.id}:${key}`)
    if (!entry) return false
    // A user can be allocated to multiple projects on the same day —
    // match against every project id collected for the cell.
    if (Array.isArray(entry.projectIds)) return entry.projectIds.includes(projectId)
    return entry.projectId === projectId
  })
}

export const VIEW_DAYS = { week: 7, fortnight: 14, month: 28 }

/**
 * Transforms raw allocation API records into a Map keyed by "userId:yyyy-MM-dd".
 *
 * @param {Array<Object>} allocations - Raw timesheet allocation records from API
 * @param {number} [standardHours=HOURS_PER_DAY] - Standard daily capacity hours
 * @returns {Map<string, Object>} Booked schedule map keyed by "userId:yyyy-MM-dd"
 */
export function buildScheduleMap(allocations, standardHours = HOURS_PER_DAY) {
  const raw = new Map()

  ;(allocations || [])
    .filter((a) => a && a.status !== 'REJECTED')
    .sort((a, b) => {
      const timeA = new Date(a.startTime || 0).getTime()
      const timeB = new Date(b.startTime || 0).getTime()
      if (timeA !== timeB) return timeA - timeB
      const createdA = new Date(a.createdAt || a.created_at || 0).getTime()
      const createdB = new Date(b.createdAt || b.created_at || 0).getTime()
      if (createdA !== createdB) return createdA - createdB
      return (a.id || '').localeCompare(b.id || '')
    })
    .forEach((a) => {
      if (!a.userId || !a.startTime) return
      // Bucket by the entry's explicit date if present, otherwise by startTime.
      // Either field is treated as naive UTC so we avoid local-timezone shifts.
      const day = (typeof a.date === 'string' && a.date.length >= 10 ? a.date.slice(0, 10) : null)
        || a.startTime.slice(0, 10)
      const key = `${a.userId}:${day}`
      const prev = raw.get(key) || {
        bookedHours:   0,
        capacityHours: standardHours,
        project:       null,
        projectId:     null,
        projectIds:    [],
        latestEndTime: null,
        entries:       [],
      }
      // Always derive hours from the real start/end time — never assume a flat standard day.
      const rawHours = Math.abs(differenceInMinutes(new Date(a.endTime), new Date(a.startTime))) / 60
      const hours = Math.round(rawHours * 10) / 10

      // Track the latest end time for the day so the scheduler can chain new entries after it.
      const latestEndTime = prev.latestEndTime && a.endTime
        ? (a.endTime > prev.latestEndTime ? a.endTime : prev.latestEndTime)
        : (a.endTime || prev.latestEndTime)

      // Fall back to the FK column when the project include resolved to null.
      const pid = a.project?.id ?? a.projectId ?? null

      raw.set(key, {
        bookedHours:   prev.bookedHours + hours, // not capped, so overlaps can be flagged as "over"
        capacityHours: standardHours,
        project:       prev.project   || (a.project?.name ?? null),
        projectId:     prev.projectId || pid,
        projectIds:    pid && !prev.projectIds.includes(pid) ? [...prev.projectIds, pid] : prev.projectIds,
        latestEndTime,
        entries:       [...(prev.entries || []), a],
      })
    })

  const map = new Map()
  raw.forEach((v, k) => {
    map.set(k, {
      ...v,
      status: v.bookedHours > v.capacityHours  ? 'over'
            : v.bookedHours === v.capacityHours ? 'full'
            : v.bookedHours > 0                 ? 'partial'
            :                                      'free',
    })
  })
  return map
}
